import {
  Component, Inject, OnDestroy, OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogRef,
} from '@angular/material/dialog';
import { CourseRequestsService } from 'src/app/services/course-requests.service';
import { EntityService } from 'src/app/services/entity.service';
import { formatDate as formatDateUtil } from 'src/app/utils/dates.utils';

@Component({
  selector: 'app-my-course-request-details',
  templateUrl: './my-course-request-details.component.html',
  styleUrls: [ './my-course-request-details.component.scss' ],
})
export class MyCourseRequestDetailsComponent implements OnInit, OnDestroy {
  course: any = {
    courseRequestId: '',
    name: '',
    creditHours: '',
    courseType: '',
    instructor: '',
    cost: '',
    webUrl: '',
    description: '',
    async: false,
    virtual: false,
    anytime: false,
    location: '',
    roomNumber: '',
    courseDate: '',
    startTime: '',
    endTime: '',
  }
  isLoading: any;
  courseRequestSubscription: any;
  showUploadDocumentsModal = false;
  autoOpenUpload = false;

  constructor(
    public dialogRef: MatDialogRef<MyCourseRequestDetailsComponent>,
    public dialog: MatDialog, @Inject(MAT_DIALOG_DATA) public data: any,
    private courseRequestsService: CourseRequestsService,
    private entityService: EntityService,
  ) { }

  ngOnInit(): void {
    this.loadCourseRequest();
  }

  loadCourseRequest(): void {
    this.isLoading = true;
    
    // Check if we should auto-open upload documents modal
    this.autoOpenUpload = this.data?.autoOpenUpload || false;
    
    this.courseRequestSubscription = this.courseRequestsService.getCourseRequest(this.data.courseId).subscribe({
      next: (response: any) => {
        try {
          // The response structure: data is the course, data.CourseItems[0] is the course item, data.CourseItems[0].Documents contains documents
          const course = response?.data || {};
          const courseItem = course?.CourseItems?.[0] || {};
          const documents = courseItem?.Documents || [];
          const timezone = this.entityService.getLocalTimezone();
          
          const isAsync = courseItem?.preRecordedOption === 'ASYNC';
          const isAnytime = courseItem?.preRecordedOption === 'ANYTIME';
          
          this.course = {
            ...course,
            ...courseItem,
            name: course?.courseName || '',
            creditHours: course?.trackingValue || '',
            courseType: course?.CourseType?.courseType || course?.courseType || '',
            instructor: courseItem?.instructor || null,
            cost: course?.courseCost || null,
            webUrl: courseItem?.hyperlink || null,
            description: course?.courseDescription || '',
            async: isAsync,
            virtual: courseItem?.virtualFlag === 1,
            anytime: isAnytime,
            location: courseItem?.location || '',
            roomNumber: courseItem?.roomNumber || null,
            courseDate: isAsync ? 'Async' : (courseItem?.dateOffered ? formatDateUtil.asDateWithTimezone(courseItem.dateOffered, timezone) : ''),
            startTime: isAnytime ? 'Anytime' : (courseItem?.sessionStartDate ? formatDateUtil.asTimeWithTimezone(courseItem.sessionStartDate, timezone) : ''),
            endTime: isAnytime ? 'Anytime' : (courseItem?.sessionEndDate ? formatDateUtil.asTimeWithTimezone(courseItem.sessionEndDate, timezone) : ''),
            // Include Documents from CourseItems[0].Documents
            Documents: documents,
            // Ensure CourseItems is included for getCourseItemId()
            CourseItems: course?.CourseItems || [],
            // Preserve both IDs - courseItemId is from courseItem.id, courseId is from course.id
            courseItemId: courseItem?.id || null,
            courseId: course?.id || courseItem?.courseId || null,
          }
          
          // Auto-open upload documents modal if requested and courseItemId exists
          if (this.autoOpenUpload && this.getCourseItemId()) {
            setTimeout(() => {
              this.openUploadDocumentsModal();
            }, 100);
          }
        } catch (error) {
          // Error processing course request data
        } finally {
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        this.isLoading = false;
      },
    });
  }

  onDocumentsUploaded(): void {
    // Reload course request data to refresh document list
    this.loadCourseRequest();
    // Close the upload modal after successful upload
    this.closeUploadDocumentsModal();
    // Emit result to parent
    this.dialogRef.close('uploaded');
  }

  getCourseItemId(): number | null {
    // Priority: courseItemId > CourseItems[0].id > id (but only if it's not the courseId)
    if (this.course?.courseItemId) {
      return Number(this.course.courseItemId);
    }
    if (this.course?.CourseItems) {
      if (Array.isArray(this.course.CourseItems) && this.course.CourseItems.length > 0) {
        return Number(this.course.CourseItems[0].id);
      }
      if (typeof this.course.CourseItems === 'object') {
        return Number(this.course.CourseItems.id);
      }
    }
    // Only use id if it's not the same as courseId (to avoid using courseId instead of courseItemId)
    if (this.course?.id && this.course?.id !== this.course?.courseId) {
      return Number(this.course.id);
    }
    if (this.course?.course_item_id) {
      return Number(this.course.course_item_id);
    }
    return null;
  }

  openUploadDocumentsModal(): void {
    this.showUploadDocumentsModal = true;
  }

  closeUploadDocumentsModal(): void {
    this.showUploadDocumentsModal = false;
  }

  cancelRequest(): void {
    this.dialogRef.close('cancel-request');
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.courseRequestSubscription.unsubscribe();
  }

}
